'use client';

import { Tooltip as BaseTooltip } from '@base-ui-components/react/tooltip';

import { cn } from '../../utils/cn';
import './tooltip.css';

interface TooltipProviderProps {
  delayDuration?: number;
  closeDelay?: number;
  children: React.ReactNode;
}

function TooltipProvider({
  delayDuration = 700,
  closeDelay,
  children,
}: TooltipProviderProps) {
  return (
    <BaseTooltip.Provider
      delay={delayDuration}
      {...(closeDelay !== undefined ? { closeDelay } : {})}
    >
      {children}
    </BaseTooltip.Provider>
  );
}

interface TooltipProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

function Tooltip({ children, ...props }: TooltipProps) {
  return <BaseTooltip.Root {...props}>{children}</BaseTooltip.Root>;
}

interface TooltipTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

function TooltipTrigger({ className, ...props }: TooltipTriggerProps) {
  return (
    <BaseTooltip.Trigger
      className={cn(className)}
      {...(props as React.ComponentProps<typeof BaseTooltip.Trigger>)}
    />
  );
}

interface TooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}

function TooltipContent({
  className,
  side = 'top',
  sideOffset = 4,
  children,
  ...props
}: TooltipContentProps) {
  return (
    <BaseTooltip.Portal>
      <BaseTooltip.Positioner side={side} sideOffset={sideOffset}>
        <BaseTooltip.Popup
          className={cn(
            'tooltip-content bg-primary text-primary-foreground z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs',
            'transition-opacity duration-150',
            'data-[starting-style]:opacity-0',
            'data-[ending-style]:opacity-0',
            className
          )}
          {...(props as React.ComponentProps<typeof BaseTooltip.Popup>)}
        >
          {children}
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  );
}

export { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent };
export type {
  TooltipProviderProps,
  TooltipProps,
  TooltipTriggerProps,
  TooltipContentProps,
};
